import { Suspense, useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useGLTF, useAnimations, Environment, ContactShadows, Center } from '@react-three/drei';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, AlertCircle, RotateCw, ZoomIn, Move, X } from 'lucide-react';

// Error state for viewer
function ViewerError({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-tertiary)',
      borderRadius: '12px',
      padding: '2rem',
      textAlign: 'center'
    }}>
      <AlertCircle size={48} color="#8b5cf6" style={{ marginBottom: '1rem' }} />
      <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
        Could not load 3D model
      </h4>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
        {message}
      </p>
      <button 
        onClick={onClose}
        className="btn btn-primary"
      >
        Back to Gallery
      </button>
    </div>
  );
}

// The actual 3D model component
function Model({ url, animated = false }: { url: string; animated?: boolean }) {
  const { scene, animations } = useGLTF(url);
  const { actions } = useAnimations(animations, scene);
  
  // Auto-play animation if available and model is flagged as animated
  useEffect(() => {
    if (animated && actions && Object.keys(actions).length > 0) {
      const firstAction = Object.values(actions)[0];
      firstAction?.play();
    }
  }, [animated, actions]);
  
  return (
    <Center>
      <primitive object={scene} scale={animated ? 1.2 : 1.5} />
    </Center>
  );
}

// Loading state
function Loader() {
  return (
    <div style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      textAlign: 'center'
    }}>
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      >
        <RotateCw size={32} color="#8b5cf6" />
      </motion.div>
      <p style={{ color: 'var(--text-muted)', marginTop: '1rem', fontSize: '0.9rem' }}>
        Loading model...
      </p>
    </div>
  );
}

// Available models with correct paths
const availableModels = [
  { name: 'T-Pose Character', file: 'models/T-pose_web.glb', size: '418 KB', original: '2.1 MB', compression: '80%' },
  { name: 'Jogging Character', file: 'models/Jogging_web.glb', size: '235 KB', original: '1.8 MB FBX', compression: '87%', animated: true },
  { name: 'Old Man Idle', file: 'models/Old Man Idle_web.glb', size: '282 KB', original: '2.1 MB FBX', compression: '87%', animated: true },
  { name: 'Doless Model', file: 'models/Doless_web.glb', size: '66 KB', original: '6.6 MB OBJ', compression: '99%' },
  { name: 'AI Character', file: 'models/ComfyUI_00003__web.glb', size: '753 KB', original: '4.9 MB', compression: '85%' },
  { name: 'David Bust (OBJ+MTL)', file: 'models/bust_of_David_web.glb', size: '6 MB', original: '29 MB OBJ', compression: '79%' },
];

export default function Model3DViewer() {
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleModelSelect = (modelFile: string) => {
    setError(null);
    setSelectedModel(modelFile);
  };

  const handleClose = () => {
    setSelectedModel(null);
    setError(null);
  };

  return (
    <section id="viewer" className="viewer-section">
      <div className="section">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Try the <span>3D Refinery</span>
        </motion.h2>

        {/* Model Selection Cards */}
        {!selectedModel && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '2rem' }}>
              Click a model to view it in 3D. These are real outputs from our refinery pipeline.
            </p>
            <div className="products-grid">
              {availableModels.map((model, index) => (
                <motion.div
                  key={model.name}
                  className="product-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => handleModelSelect(model.file)}
                  style={{ cursor: 'pointer' }}
                  whileHover={{ y: -4 }}
                >
                  <div className="product-icon">
                    <Box size={28} />
                  </div>
                  <h3>{model.name}</h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                    {model.original} → {model.size}
                  </p>
                  {model.animated && (
                    <span style={{
                      background: 'rgba(6, 182, 212, 0.2)',
                      color: '#06b6d4',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      marginTop: '0.5rem',
                      display: 'inline-block'
                    }}>
                      ▶ Animated
                    </span>
                  )}
                  <div style={{
                    marginTop: '0.75rem',
                    padding: '0.4rem 0.75rem',
                    background: 'rgba(139, 92, 246, 0.1)',
                    borderRadius: '6px',
                    display: 'inline-block'
                  }}>
                    <span style={{ color: '#8b5cf6', fontSize: '0.8rem', fontWeight: 600 }}>
                      Draco: -{model.compression} smaller
                    </span>
                  </div>
                  <div style={{ 
                    marginTop: '1rem',
                    padding: '0.5rem 1rem',
                    background: 'rgba(139, 92, 246, 0.1)',
                    borderRadius: '6px',
                    display: 'inline-block'
                  }}>
                    <span style={{ color: '#8b5cf6', fontSize: '0.85rem', fontWeight: 600 }}>
                      Click to View 3D →
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* 3D Viewer */}
        <AnimatePresence>
          {selectedModel && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              style={{
                marginTop: '2rem',
                background: 'var(--bg-secondary)',
                borderRadius: '16px',
                border: '1px solid var(--border)',
                overflow: 'hidden'
              }}
            >
              {/* Viewer Header */}
              <div style={{
                padding: '1rem 1.5rem',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h3 style={{ color: 'var(--text-primary)', fontSize: '1.1rem' }}>
                    3D Model Viewer
                  </h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    Drag to rotate • Scroll to zoom • Right-click to pan
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  style={{
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)'
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* 3D Canvas */}
              <div style={{ height: '500px', position: 'relative' }}>
                {error ? (
                  <ViewerError message={error} onClose={handleClose} />
                ) : (
                  <Canvas
                    camera={{ position: [0, 0, 5], fov: 45 }}
                    style={{ background: 'var(--bg-tertiary)' }}
                  >
                    <Suspense fallback={null}>
                      <ambientLight intensity={0.5} />
                      <directionalLight position={[10, 10, 5]} intensity={1} />
                      <Environment preset="city" />
                      <Model url={selectedModel} animated={availableModels.find(m => m.file === selectedModel)?.animated} />
                      <ContactShadows 
                        position={[0, -1.5, 0]} 
                        opacity={0.4} 
                        scale={10} 
                        blur={2} 
                        far={4}
                      />
                    </Suspense>
                    <OrbitControls 
                      enablePan={true}
                      enableZoom={true}
                      enableRotate={true}
                      minDistance={2}
                      maxDistance={10}
                    />
                  </Canvas>
                )}
                
                {/* Loader overlay - only show when no error */}
                {!error && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Suspense fallback={<Loader />}>
                      <div /> 
                    </Suspense>
                  </div>
                )}

                {/* Controls hint */}
                <div style={{
                  position: 'absolute',
                  bottom: '1rem',
                  left: '1rem',
                  display: 'flex',
                  gap: '1rem'
                }}>
                  <div style={{
                    background: 'rgba(0,0,0,0.5)',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: 'white',
                    fontSize: '0.8rem'
                  }}>
                    <Move size={14} /> Rotate
                  </div>
                  <div style={{
                    background: 'rgba(0,0,0,0.5)',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: 'white',
                    fontSize: '0.8rem'
                  }}>
                    <ZoomIn size={14} /> Zoom
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* What You Get */}
        {!selectedModel && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            style={{
              marginTop: '4rem',
              padding: '3rem',
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              border: '1px solid var(--border)'
            }}
          >
            <h3 style={{ textAlign: 'center', marginBottom: '2rem', color: 'var(--text-primary)' }}>
              What You Get from the Refinery
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '2rem'
            }}>
              {[
                { title: 'Clean USD', desc: 'Omniverse & Houdini ready', icon: '🎯' },
                { title: 'Web GLB', desc: 'Draco compressed, 84% smaller', icon: '🌐' },
                { title: 'Full GLB', desc: 'Maximum fidelity version', icon: '✨' },
                { title: 'Validation Report', desc: 'Technical specs & metrics', icon: '📊' }
              ].map((item, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>{item.icon}</div>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.25rem' }}>{item.title}</h4>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
